// Canvas renderer. Reads game state, listens to game events for effects.
// The Hero, and any enemy/Giant listed in src/data/biomes.ts, use painted sprites; everything else
// (and anything still loading) draws procedural placeholders. Layout math is shared by both.
import { fmt } from '../core/decimal';
import { BIOMES, biomeForStage, hitImage, monsterImage } from '../data/biomes';

/** First zone with painted art; stands in for zones whose art isn't done yet. */
const FALLBACK_ZONE = BIOMES.find((z) => z.image);
import { HEROES, describeHeroSkill, type HeroDef } from '../data/heroes';
import type { Game, Pos } from '../game/game';
import { PET_BY_ID } from '../data/pets';
import { GUARDIAN_ART, type GuardianShot } from '../data/heroArt';

interface FloatText { x: number; y: number; vy: number; life: number; text: string; color: string; size: number }
interface Particle { x: number; y: number; vx: number; vy: number; life: number; color: string; size: number }
/** A Guardian volley in flight: a signature shot (heroArt.ts), else a slash crescent (melee) or an orb. */
interface Shot {
  x: number; y: number; tx: number; ty: number; t: number; color: string; kind: GuardianShot | 'slash' | 'orb';
  /** Painted projectile / impact strip (heroArt.ts), drawn instead of the code shape once loaded. */
  image?: string; impact?: string; spin?: boolean;
}
/** A painted impact burst playing at a hit point. */
interface Impact { x: number; y: number; t: number; src: string }
interface Coin { x: number; y: number; vx: number; vy: number; t: number; delay: number }
interface Slash { x: number; y: number; a: number; life: number }
/** A Guardian's place in the arc around the enemy; `scale` shrinks places further back. */
interface HeroSlot { h: HeroDef; x: number; y: number; side: number; phase: number; scale: number }
/** A painted sprite trimmed to its visible pixels, plus a white silhouette for the hit flash. */
/** A sprite cropped to its visible pixels; (x0, y0) is where the crop sat in the original canvas. */
interface TrimmedSprite { img: HTMLCanvasElement; flash: HTMLCanvasElement; x0: number; y0: number }
/** A 512-cell animation strip plus the visible height of its first frame, used to size it. */
interface Strip { img: HTMLImageElement; bodyH: number }

const FONT = '"Trebuchet MS", system-ui, sans-serif';

/** Tuck look3 seen from behind (arena view, handoff/art-inbox/batch2): 512px cells, feet at (256, 490), body ~309px tall. */
const PLAYER_IDLE = { src: 'art/player_look3_back_idle.png', cell: 512, frames: 4, fps: 8, footX: 256, footY: 490, bodyH: 311 };
/** Enemy hit pose: shown this long after a hit, and at most once per gap. */
const HIT_POSE_SEC = 0.16, HIT_POSE_GAP = 0.4;
/** Painted Guardian impact strips: 4 cells of 256x256, played over ~0.25 s. */
const IMPACT_FRAMES = 4, IMPACT_FPS = 16;
/** Block Knight's brick chips: his blue, lighter blue, silver and gold. */
const BRICK_CHIPS = ['#2f5fd0', '#4f7ff0', '#c9ced8', '#f2c14e'];
/**
 * The arena (handoff/concept-art/00-layout-mockup-v3-CURRENT): the enemy at the back centre, the Hero
 * in front of it, Guardians in an arc on both sides. Depths are offsets from the room's floor line
 * as fractions of the battlefield height; arc places are per side, nearest first, x as a fraction of
 * the half play width from the centre.
 */
const ARENA = {
  monsterDepth: -0.02,
  heroDepth: 0.10,
  heroScale: 1.05,
  arc: [
    { x: 0.50, depth: 0.035, scale: 1.0 },
    { x: 0.68, depth: -0.005, scale: 0.92 },
    { x: 0.58, depth: -0.07, scale: 0.84 },
    { x: 0.80, depth: 0.045, scale: 1.0 },
    { x: 0.95, depth: -0.035, scale: 0.84 }, // out past the others so a full team doesn't stack up
  ],
  /** Tall, narrow battlefields (phone portrait): too little width for one arc, so two rows per side. */
  arcNarrow: [
    { x: 0.50, depth: 0.075, scale: 0.95 },
    { x: 0.84, depth: 0.06, scale: 0.92 },
    { x: 0.62, depth: -0.035, scale: 0.8 },
    { x: 0.88, depth: -0.05, scale: 0.78 },
    { x: 0.40, depth: -0.075, scale: 0.72 },
  ],
};
/**
 * The room art arrives already muted (sharp arena rug, soft dim room), so the code only darkens the
 * edges and lights the fight; `filter` is for rooms painted without that treatment.
 */
const BACKDROP = { filter: 'none', vignette: 0.45, spotlight: 0.1 };

/** Paper Plane Gift on screen (CSS px): plane 108x67.5, box 44x44; tail anchor from Codex's NOTES (29,191) scaled to the plane. */
const GIFT = { planeW: 108, planeH: 67.5, boxW: 44, boxH: 44, tail: { x: 9.8, y: 64.5 }, string: 30 };

/** Seconds an announcement stays up (it fades over the last half second). */
const BANNER_LIFE = 2.2;
/** Row height of stacked announcements: the banner art plus a little air. */
const BANNER_GAP = 42;

/** Guardian strips (src/data/heroArt.ts): same cell and anchor as the Hero's. */
const GUARDIAN_STRIP = { cell: 512, footX: 256, footY: 490, idleFrames: 4, idleFps: 8, attackFrames: 8, attackFps: 16, strikeFrame: 3 };
/** Attack strip, same cell and anchor. 0-1 anticipation, 2-3 strike, 4-5 impact, 6-7 recover (7 = idle frame 0). */
const PLAYER_ATTACK = { ...PLAYER_IDLE, src: 'art/player_look3_back_attack.png', frames: 8, fps: 18 };

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private W = 0;
  private H = 0;
  private texts: FloatText[] = [];
  /** Announcement queue, laid out top-down every frame (newest at the bottom). */
  private banners: FloatText[] = [];
  private bannerArt = loadSprite('art/ui/announcement_banner.png');
  private giftArt = { plane: loadSprite('art/gift/gift_plane.webp'), box: loadSprite('art/gift/gift_box.webp'),
    glow: loadSprite('art/gift/gift_glow.webp'), burst: loadSprite('art/gift/gift_catch_burst.webp') };
  private giftBursts: { x: number; y: number; t: number }[] = [];
  private lastDt = 0;
  private particles: Particle[] = [];
  private shots: Shot[] = [];
  private coins: Coin[] = [];
  /** Where flying coins go: the coin pill's centre in canvas pixels (set by the UI). */
  private coinTarget: Pos = { x: 0, y: 0 };
  private coinArt = loadSprite('art/cur_coin.png');
  /** Called as each coin reaches the pill (the UI bounces it). */
  onCoinLanded: (() => void) | null = null;
  setCoinTarget(p: Pos): void { this.coinTarget = p; }
  private slashes: Slash[] = [];
  private impacts: Impact[] = [];
  /** Painted volley FX by path: undefined = not requested, null = loading or failed. */
  private fxImages = new Map<string, HTMLImageElement | null>();
  private shake = 0;
  private flash = 0;
  private heroClock = 0;
  private petLunge = 0;
  /** 1 -> 0 after each tap: drives the code-drawn Hero's lunge and blade swing. */
  private playerSwing = 0;
  /** Current frame (fractional) of the Hero's attack strip; -1 when idling. */
  private attackF = -1;
  private lunge = new Map<string, number>();
  /** Current frame (fractional) of each Guardian's attack strip; absent when idling. */
  private guardianAttackF = new Map<string, number>();
  private strips = new Map<string, Strip | null>();
  // per-monster animation state (reset on spawn)
  private hitT = 0;
  /** When the current hit-pose flinch started (seconds, performance clock). */
  private hitPoseAt = -1;
  private bornT = 0;
  private dieT = 0;

  constructor(private game: Game, private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    this.resize();
    new ResizeObserver(() => this.resize()).observe(canvas);
    this.bindEvents();
  }

  // ---- Layout -------------------------------------------------------------------

  resize(): void {
    const r = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.W = r.width;
    this.H = r.height;
    this.canvas.width = Math.round(this.W * dpr);
    this.canvas.height = Math.round(this.H * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.dpr = dpr;
    this.backdrop = null;
    this.slotCache = null; // slot positions depend on canvas size
  }

  private dpr = 1;
  /** The muted room art, rendered once per image and canvas size (a full-screen blur is too costly per frame). */
  private backdrop: { key: string; canvas: HTMLCanvasElement } | null = null;

  /** Where the enemy stands: just behind the floor line. */
  private monsterFloor() { return this.groundY() + this.H * ARENA.monsterDepth; }
  /** Where the Hero stands: in front of the enemy, nearer the camera. */
  private heroFloor() { return this.groundY() + this.H * ARENA.heroDepth; }

  private groundY() {
    const b = biomeForStage(this.game.state.stage);
    return this.H * ((b.image ? b : FALLBACK_ZONE)?.groundAt ?? 0.8);
  }
  private zoneImages = new Map<string, HTMLImageElement>();

  private zoneImage(src: string): HTMLImageElement {
    let img = this.zoneImages.get(src);
    if (!img) { img = loadSprite(src); this.zoneImages.set(src, img); }
    return img;
  }

  /**
   * Painted zone background, scaled to cover the battlefield. The image's floor line (groundAt) is
   * kept on the canvas floor line even when the canvas is wider than the art and must crop vertically.
   */
  private drawZoneImage(img: HTMLImageElement, groundAt: number): void {
    const { ctx, W, H } = this;
    const pad = 16; // slack for screen shake
    const scale = Math.max((W + pad * 2) / img.naturalWidth, (H + pad * 2) / img.naturalHeight);
    const dw = img.naturalWidth * scale, dh = img.naturalHeight * scale;
    const dx = (W - dw) / 2;
    const dy = Math.min(-pad + 0, Math.max(H + pad - dh, groundAt * H - groundAt * dh));
    const key = `${img.src}|${Math.round(dw)}x${Math.round(dh)}|${this.dpr}`;
    if (this.backdrop?.key !== key) {
      const c = document.createElement('canvas');
      c.width = Math.round(dw * this.dpr); c.height = Math.round(dh * this.dpr);
      const bctx = c.getContext('2d')!;
      bctx.filter = BACKDROP.filter; // browsers without canvas filters still get the vignette below
      bctx.drawImage(img, 0, 0, c.width, c.height);
      this.backdrop = { key, canvas: c };
    }
    ctx.drawImage(this.backdrop.canvas, dx, dy, dw, dh);
    this.drawArenaLight();
  }

  /** A warm pool of light where the fight is and a dark vignette around it, so the eye lands on the fight. */
  private drawArenaLight(): void {
    const { ctx, W, H } = this;
    const cx = this.cx(), gy = this.groundY();
    ctx.save();
    ctx.translate(cx, gy);
    ctx.scale(1, 0.32);
    const pool = ctx.createRadialGradient(0, 0, 0, 0, 0, this.playW() * 0.5);
    pool.addColorStop(0, `rgba(255,214,150,${BACKDROP.spotlight})`);
    pool.addColorStop(1, 'rgba(255,214,150,0)');
    ctx.fillStyle = pool;
    ctx.fillRect(-this.playW(), -this.playW(), this.playW() * 2, this.playW() * 2);
    ctx.restore();
    const v = ctx.createRadialGradient(cx, gy - H * 0.2, Math.min(this.playW(), H) * 0.35, cx, gy - H * 0.2, Math.max(W, H) * 0.8);
    v.addColorStop(0, 'rgba(8,6,20,0)');
    v.addColorStop(1, `rgba(8,6,20,${BACKDROP.vignette})`);
    ctx.fillStyle = v;
    ctx.fillRect(-20, -20, W + 40, H + 40);
  }
  /** Pixels on the right covered by the floating panel; the fight is centred in what's left. */
  private rightInset = 0;
  setRightInset(px: number): void {
    if (Math.abs(px - this.rightInset) < 1) return;
    this.rightInset = px;
    this.slotCache = null;
  }
  /** Baseline of the first announcement: a third of the way down, but never over the HUD. */
  private bannerTop() { return Math.max(this.H * 0.36, this.topInset + 28); }
  /** Announcements shrink on narrow battlefields so a burst of them doesn't bury the fight. */
  private bannerScale() { return Math.max(0.62, Math.min(1, this.playW() / 640)); }
  private bannerGap() { return BANNER_GAP * this.bannerScale(); }
  /** Bottom of the HUD over the battlefield (enemy bar and Giant timer): monsters stay below it. */
  private topInset = 0;
  setTopInset(px: number): void { this.topInset = px; }
  /** Width of the visible play area and its horizontal centre. */
  private playW() { return Math.max(200, this.W - this.rightInset); }
  private cx() { return this.playW() / 2; }

  private baseRadius() { return Math.min(this.playW(), this.H) * 0.18; }
  /** Monsters tower over the toy heroes (docs/DESIGN.md §6.4): regular ~44% of battle height, Giants ~50%. */
  private monsterRadius() {
    const want = Math.min(this.playW(), this.H) * (this.game.monster.boss ? 0.26 : 0.23);
    const room = (this.monsterFloor() - this.topInset) / 2.05; // sprite is ~1.9r tall, plus the Giant's hop
    return Math.max(want * 0.75, Math.min(want, room));
  }
  /** The procedural stand-in Giant's horns and crown stick out above its body: keep it clear of the HUD. */
  private blobRadius() { return this.game.monster.boss ? Math.min(this.playW(), this.H) * 0.22 : this.monsterRadius(); }
  monsterCenter(): Pos { return { x: this.cx(), y: this.monsterFloor() - this.monsterRadius() * 0.95 }; }

  /** Random point on the monster, for keyboard/auto taps. */
  randomMonsterPoint(): Pos {
    const p = this.monsterCenter(), r = this.monsterRadius();
    return { x: p.x + (Math.random() - 0.5) * r, y: p.y + (Math.random() - 0.5) * r };
  }

  private slotCache: HeroSlot[] | null = null;
  private static readonly MAX_VISIBLE_HEROES = 10;

  /** On-screen heroes: the strongest few, kept in roster order. Recomputed only when purchases change. */
  private heroSlots(): HeroSlot[] {
    if (this.slotCache) return this.slotCache;
    const ranked = HEROES.filter((h) => this.game.heroLevel(h.id) > 0)
      .map((h) => ({ h, dps: this.game.heroDpsOf(h) }))
      .sort((a, b) => b.dps.cmp(a.dps))
      .slice(0, Renderer.MAX_VISIBLE_HEROES)
      .map((x) => x.h);
    const owned = HEROES.filter((h) => ranked.includes(h));
    this.slotCache = this.layoutSlots(owned);
    return this.slotCache;
  }

  /** Guardians alternate sides, filling each side's arc from the nearest place outwards. */
  private layoutSlots(owned: HeroDef[]): HeroSlot[] {
    // Keep a Guardian's whole body on screen: its half-width is about 0.4 of the base radius.
    const gy = this.groundY(), half = this.cx(), margin = Math.max(24, this.baseRadius() * 0.4);
    const arc = this.playW() < this.H ? ARENA.arcNarrow : ARENA.arc;
    return owned.map((h, i) => {
      const side = i % 2 === 0 ? -1 : 1;
      const place = arc[Math.floor(i / 2) % arc.length]!;
      const x = Math.max(margin, Math.min(this.playW() - margin, this.cx() + side * place.x * half));
      // y is 12px above the feet, as the code-drawn Guardians expect.
      return { h, x, y: gy + this.H * place.depth - 12, side, phase: (i * 0.37) % 1, scale: place.scale };
    });
  }

  // ---- Events -------------------------------------------------------------------

  private bindEvents(): void {
    const g = this.game.events;
    g.on('spawn', () => { this.hitT = 0; this.hitPoseAt = -1; this.bornT = 0; this.dieT = 0; });
    g.on('damage', (e) => {
      if (e.source === 'tap' || e.source === 'auto' || e.source === 'skill') { this.playerSwing = 1; this.startAttack(); }
      this.hitT = 1;
      // Hit pose: a short flinch, at most every HIT_POSE_GAP so rapid taps don't freeze it.
      const now = performance.now() / 1000;
      if (now - this.hitPoseAt > HIT_POSE_GAP) this.hitPoseAt = now;
      const pos = e.pos ?? (e.source === 'skill' ? { x: this.cx(), y: this.monsterCenter().y - this.monsterRadius() } : this.randomMonsterPoint());
      this.texts.push({ x: pos.x + (Math.random() - 0.5) * 30, y: pos.y, vy: -90, life: 0.9, text: fmt(e.amount),
        color: e.source === 'pet' ? '#7fe0ff' : e.crit ? '#ff4d6d' : '#fff', size: e.crit || e.source === 'pet' ? 28 : 20 });
      if (e.source !== 'skill') this.slashes.push({ x: pos.x, y: pos.y, a: Math.random() * Math.PI, life: 0.15 });
      if (e.crit) this.shake = Math.max(this.shake, 6);
      const hue = this.game.monster.seed * 37 % 360;
      for (let i = 0; i < (e.crit ? 10 : 4); i++) {
        const a = Math.random() * Math.PI * 2, s = 80 + Math.random() * 160;
        this.particles.push({ x: pos.x, y: pos.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 60, life: 0.4,
          color: `hsl(${hue},70%,60%)`, size: 3 + Math.random() * 3 });
      }
    });
    g.on('kill', (e) => {
      // Splash-through can kill dozens per frame: cap effects so bursts stay readable and cheap.
      if (this.coins.length > 80) return;
      const p = this.monsterCenter();
      for (let i = 0; i < (e.monster.boss ? 14 : 5); i++) {
        this.coins.push({ x: p.x + (Math.random() - 0.5) * 60, y: p.y, vx: (Math.random() - 0.5) * 260,
          vy: -200 - Math.random() * 200, t: 0, delay: 0.35 + Math.random() * 0.2 });
      }
      this.text(p.x, p.y - this.monsterRadius() - 10, '+' + fmt(e.gold), '#ffcc4d', 22, -40, 1.2);
    });
    g.on('stageCleared', () => { this.shake = 12; this.slotCache = null; }); // zone change can move the floor
    g.on('petAttack', () => { this.petLunge = 1; });
    g.on('bossFailed', () => this.banner('The Giant got away!', '#ff5c6c', 26));
    g.on('heroHired', (e) => this.banner(`${e.hero.name} joins the brigade!`, e.hero.color, 22));
    g.on('heroSkillUnlocked', (e) => this.banner(`${e.hero.name}: ${describeHeroSkill(e.hero, e.skill.effect)}`, '#ffcc4d', 17));
    const dropSlots = () => { this.slotCache = null; };
    g.on('purchase', dropSlots);
    g.on('prestige', dropSlots);
    g.on('spawn', dropSlots); // cheap, and covers setState/load
    g.on('skillUsed', (e) => {
      this.banner(e.skill.name + '!', '#ffcc4d', 24);
      if (e.skill.id === 'strike') { this.flash = 1; this.shake = 14; }
    });
  }

  text(x: number, y: number, text: string, color = '#fff', size = 18, vy = -20, life = 1.5): void {
    this.texts.push({ x, y, vy, life, text, color, size });
  }

  /**
   * Announcement line (hero joined, skill unlocked, boss escaped...). Banners stack downward
   * instead of overlapping, and only the newest few are kept when many fire at once.
   */
  banner(text: string, color = '#ffcc4d', size = 20): void {
    const MAX = 4;
    while (this.banners.length >= MAX) this.banners.shift();
    // Snap survivors to their rows so a burst never shows lines sliding over each other.
    this.banners.forEach((b, i) => { b.y = this.bannerTop() + i * this.bannerGap(); });
    const y = this.bannerTop() + this.banners.length * this.bannerGap();
    this.banners.push({ x: this.cx(), y, vy: 0, life: BANNER_LIFE, text, color, size: Math.round(size * this.bannerScale()) });
  }

  // ---- Update -------------------------------------------------------------------

  update(dt: number): void {
    this.lastDt = dt;
    const m = this.game.monster;
    this.bornT = Math.min(1, this.bornT + dt * 4);
    this.hitT = Math.max(0, this.hitT - dt * 6);
    if (m.dead) this.dieT = Math.min(1, this.dieT + dt * 3);

    if (!m.dead) {
      const prev = this.heroClock;
      this.heroClock += dt;
      const p = this.monsterCenter();
      for (const s of this.heroSlots()) {
        // One visual volley per hero per second; actual damage is continuous in the core.
        if (Math.floor(prev + s.phase) !== Math.floor(this.heroClock + s.phase)) {
          this.lunge.set(s.h.id, 1);
          // Painted Guardians release their shot on the strike frame of their attack strip.
          // A long attack still playing isn't restarted; it fires on its own strike frame.
          if (GUARDIAN_ART[s.h.id]?.attack && this.strips.get(GUARDIAN_ART[s.h.id]!.attack!)) {
            if (!this.guardianAttackF.has(s.h.id)) this.guardianAttackF.set(s.h.id, 0);
          }
          else this.fireShot(s, s.x, s.y - 18);
        }
      }
    }
    for (const [id, v] of this.lunge) this.lunge.set(id, Math.max(0, v - dt * 5));
    for (const [id, f] of this.guardianAttackF) {
      const a = GUARDIAN_ART[id], frames = a?.attackFrames ?? GUARDIAN_STRIP.attackFrames;
      const strike = a?.strikeFrame ?? GUARDIAN_STRIP.strikeFrame;
      const next = f + dt * (a?.attackFps ?? GUARDIAN_STRIP.attackFps);
      if (f < strike && next >= strike) {
        const slot = this.heroSlots().find((o) => o.h.id === id);
        // From the weapon: in front of the Guardian at about chest height.
        const h = this.baseRadius() * 0.8 * (slot?.scale ?? 1);
        if (slot) this.fireShot(slot, slot.x - slot.side * h * 0.4, slot.y + 12 - h * 0.55);
      }
      if (next >= frames) this.guardianAttackF.delete(id); else this.guardianAttackF.set(id, next);
    }

    step(this.texts, dt, (o) => { o.y += o.vy * dt; o.vy *= 0.96; });
    step(this.banners, dt, () => {});
    this.banners.forEach((b, i) => { b.y += (this.bannerTop() + i * this.bannerGap() - b.y) * Math.min(1, dt * 12); });
    step(this.particles, dt, (o) => { o.x += o.vx * dt; o.y += o.vy * dt; o.vy += 500 * dt; });
    step(this.slashes, dt, () => {});
    for (let i = this.impacts.length - 1; i >= 0; i--) {
      const o = this.impacts[i]!;
      o.t += dt * IMPACT_FPS;
      if (o.t >= IMPACT_FRAMES) this.impacts.splice(i, 1);
    }
    for (let i = this.shots.length - 1; i >= 0; i--) {
      const s = this.shots[i]!;
      s.t += dt * 3.2;
      if (s.t >= 1) {
        if (s.impact && this.fx(s.impact)) {
          this.impacts.push({ x: s.tx, y: s.ty, t: 0, src: s.impact });
        } else if (s.kind === 'brick') {
          // Shatters into brick chips in the Knight's blue, silver and gold, which bounce and fall.
          for (let k = 0; k < 10; k++) this.particles.push({ x: s.tx, y: s.ty, vx: (Math.random() - 0.5) * 320,
            vy: -60 - Math.random() * 220, life: 0.45, color: BRICK_CHIPS[k % BRICK_CHIPS.length]!, size: 4 + (k % 3) * 2 });
        } else {
          for (let k = 0; k < 6; k++) this.particles.push({ x: s.tx, y: s.ty, vx: (Math.random() - 0.5) * 180,
            vy: (Math.random() - 0.5) * 180, life: 0.3, color: k % 2 ? '#fff' : s.color, size: 4 });
        }
        if (s.kind !== 'orb') this.slashes.push({ x: s.tx, y: s.ty, a: Math.random() * Math.PI * 2, life: 0.15 });
        this.shots.splice(i, 1);
      }
    }
    const gy = this.groundY();
    for (let i = this.coins.length - 1; i >= 0; i--) {
      const c = this.coins[i]!;
      c.t += dt;
      if (c.t < c.delay) {
        c.x += c.vx * dt; c.y += c.vy * dt; c.vy += 900 * dt; c.vx *= 0.97;
        if (c.y > gy) { c.y = gy; c.vy *= -0.4; }
      } else {
        // Home in on the coin pill (top right), speeding up as it goes.
        const k = Math.min(1, dt * (6 + (c.t - c.delay) * 14)), tx = this.coinTarget.x, ty = this.coinTarget.y;
        c.x += (tx - c.x) * k; c.y += (ty - c.y) * k;
        if (Math.hypot(tx - c.x, ty - c.y) < 14) { this.coins.splice(i, 1); this.onCoinLanded?.(); }
      }
    }
    this.petLunge = Math.max(0, this.petLunge - dt * 4);
    this.playerSwing = Math.max(0, this.playerSwing - dt * 7);
    if (this.attackF >= 0) { this.attackF += dt * PLAYER_ATTACK.fps; if (this.attackF >= PLAYER_ATTACK.frames) this.attackF = -1; }
    this.shake = Math.max(0, this.shake - dt * 40);
    this.flash = Math.max(0, this.flash - dt * 3);
  }

  // ---- Draw ---------------------------------------------------------------------

  draw(): void {
    const { ctx, W, H } = this;
    const b = biomeForStage(this.game.state.stage);
    const gy = this.groundY();
    ctx.save();
    if (this.shake > 0) ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);

    // Zones without their own art yet borrow the first painted zone, so the game never shows bare
    // placeholders once any background exists. Each zone switches to its own art as soon as it's added.
    const painted = b.image ? b : FALLBACK_ZONE;
    const art = painted?.image ? this.zoneImage(painted.image) : null;
    if (art && art.complete && art.naturalWidth) {
      this.drawZoneImage(art, painted!.groundAt ?? 0.8);
    } else {
      // Procedural fallback (zones without art yet, or while the image loads).
      const sky = ctx.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, b.sky[0]); sky.addColorStop(1, b.sky[1]);
      ctx.fillStyle = sky; ctx.fillRect(-20, -20, W + 40, H + 40);
      ctx.fillStyle = 'rgba(0,0,0,0.12)';
      ctx.beginPath(); ctx.moveTo(-20, gy);
      for (let x = -20; x <= W + 20; x += 20) ctx.lineTo(x, gy - 40 - Math.sin(x * 0.012 + this.game.state.stage) * 25);
      ctx.lineTo(W + 20, gy); ctx.fill();
      ctx.fillStyle = b.ground; ctx.fillRect(-20, gy, W + 40, H);
      ctx.fillStyle = 'rgba(0,0,0,0.15)'; ctx.fillRect(-20, gy, W + 40, 4);
    }

    // Back to front by where each one stands: Guardians behind the enemy, the enemy, the rest, the Hero.
    const slots = [...this.heroSlots()].sort((a, b) => a.y - b.y);
    const mf = this.monsterFloor();
    for (const s of slots) if (s.y + 12 < mf) this.drawHero(s);
    this.drawMonster();
    for (const s of slots) if (s.y + 12 >= mf) this.drawHero(s);
    this.drawPlayer();
    this.drawPet();
    this.drawSupplyDrop();
    this.drawGiftBursts(this.lastDt);

    for (const s of this.shots) this.drawShot(s);
    for (const o of this.impacts) {
      const img = this.fx(o.src), size = this.baseRadius() * 0.9;
      if (img) ctx.drawImage(img, Math.floor(o.t) * 256, 0, 256, 256, o.x - size / 2, o.y - size / 2, size, size);
    }
    for (const p of this.particles) {
      ctx.globalAlpha = Math.min(1, p.life * 3); ctx.fillStyle = p.color; ctx.fillRect(p.x, p.y, p.size, p.size);
    }
    ctx.globalAlpha = 1;
    for (const s of this.slashes) {
      ctx.save(); ctx.translate(s.x, s.y); ctx.rotate(s.a);
      ctx.strokeStyle = `rgba(255,255,255,${s.life / 0.15})`; ctx.lineWidth = 4; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.arc(0, 0, 34, -0.9, 0.9); ctx.stroke(); ctx.restore();
    }
    for (const c of this.coins) this.drawCoinArt(c);
    ctx.textAlign = 'center'; ctx.lineJoin = 'round';
    for (const t of this.texts) this.drawFloatText(t);
    // Announcements sit on the painted banner (a dark pill until it loads) so they read over any room.
    for (const t of this.banners) {
      const age = BANNER_LIFE - t.life, pop = age < 0.18 ? 0.75 + 0.25 * Math.sin((age / 0.18) * Math.PI / 2) : 1;
      ctx.save();
      ctx.translate(t.x, t.y - t.size * 0.35); ctx.scale(pop, pop); ctx.translate(-t.x, -(t.y - t.size * 0.35));
      ctx.globalAlpha = Math.min(1, t.life * 2);
      ctx.font = `800 ${t.size}px ${FONT}`;
      const w = ctx.measureText(t.text).width + t.size * 2, h = t.size + 18, top = t.y - t.size * 0.85 - 9;
      if (ready(this.bannerArt)) this.drawBannerArt(t.x - w / 2, top, w, h);
      else { ctx.globalAlpha *= 0.55; ctx.fillStyle = '#0b0914'; this.roundRect(t.x - w / 2, top, w, h, h / 2); ctx.fill(); }
      this.drawFloatText(t);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
    if (this.flash > 0) { ctx.fillStyle = `rgba(255,255,230,${this.flash * 0.6})`; ctx.fillRect(0, 0, W, H); }
  }

  private drawFloatText(t: FloatText): void {
    const { ctx } = this;
    ctx.globalAlpha = Math.min(1, t.life * 2);
    ctx.font = `800 ${t.size}px ${FONT}`;
    ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(0,0,0,.7)'; ctx.strokeText(t.text, t.x, t.y);
    ctx.fillStyle = t.color; ctx.fillText(t.text, t.x, t.y);
  }

  /** The announcement banner, 3-sliced: round caps at full height, the middle stretched to fit the text. */
  private drawBannerArt(x: number, y: number, w: number, h: number): void {
    const img = this.bannerArt, sh = img.naturalHeight, cap = sh / 2, dc = h / 2;
    const { ctx } = this;
    ctx.drawImage(img, 0, 0, cap, sh, x, y, dc, h);
    ctx.drawImage(img, cap, 0, img.naturalWidth - cap * 2, sh, x + dc, y, Math.max(0, w - dc * 2), h);
    ctx.drawImage(img, img.naturalWidth - cap, 0, cap, sh, x + w - dc, y, dc, h);
  }

  /** Where the active Paper Plane Gift (supply drop) is right now (it crosses the sky left -> right), or null. */
  supplyDropPos(): Pos | null {
    const d = this.game.meta.supplyDrop;
    if (!d) return null;
    const t = Math.min(1, Math.max(0, (this.game.now() - d.spawnedAt) / (d.expiresAt - d.spawnedAt)));
    return { x: -40 + (this.playW() + 80) * t, y: this.H * 0.4 + Math.sin(t * 14) * 8 };
  }

  /** True if a click at `pos` grabs the Supply Drop (generous radius: it's moving). */
  /** Where the gift is caught: the burst plays there. */
  hitSupplyDrop(pos: Pos): boolean {
    const p = this.supplyDropPos();
    return !!p && Math.hypot(pos.x - p.x, pos.y - (p.y + 22)) < 72; // the glow ring, generous: it moves
  }

  private drawSupplyDrop(): void {
    const p = this.supplyDropPos();
    if (!p) return;
    const { ctx } = this;
    const t = performance.now() / 1000, pulse = (t / 0.6) % 1;
    const G = GIFT;
    ctx.save();
    // Glow ring behind, gently pulsing (Codex batch 4 gift art; emoji fallback while it loads).
    const ring = 150 + Math.sin(t * 5) * 8;
    if (ready(this.giftArt.glow)) {
      ctx.globalAlpha = 0.75 + 0.25 * Math.sin(t * 5);
      ctx.drawImage(this.giftArt.glow, p.x - ring / 2 + 4, p.y + 22 - ring / 2, ring, ring); // centred between plane and box
      ctx.globalAlpha = 1;
    } else {
      ctx.strokeStyle = `rgba(127,224,255,${1 - pulse})`; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(p.x, p.y + 16, 34 + pulse * 20, 0, Math.PI * 2); ctx.stroke();
    }
    if (ready(this.giftArt.plane) && ready(this.giftArt.box)) {
      const tilt = Math.sin(t * 3) * 0.06;
      // Plane centred on p, tilting with its bob; the box hangs below and swings a little behind.
      const px = p.x - G.planeW / 2, py = p.y - G.planeH / 2;
      const tail = rotateAbout(px + G.tail.x, py + G.tail.y, p.x, p.y, tilt);
      const swing = Math.sin(t * 3 - 0.8) * 7;
      const bx = tail.x + 10 + swing, by = tail.y + G.string;
      // Runtime string (sways with the box).
      ctx.strokeStyle = 'rgba(70,52,34,.9)'; ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.moveTo(tail.x, tail.y);
      ctx.quadraticCurveTo(tail.x + 2 + swing * 0.3, (tail.y + by) / 2, bx, by - G.boxH * 0.38); ctx.stroke();
      ctx.save(); ctx.translate(bx, by); ctx.rotate(swing * 0.02);
      ctx.drawImage(this.giftArt.box, -G.boxW / 2, -G.boxH / 2, G.boxW, G.boxH);
      ctx.restore();
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(tilt);
      ctx.drawImage(this.giftArt.plane, -G.planeW / 2, -G.planeH / 2, G.planeW, G.planeH);
      ctx.restore();
    } else {
      ctx.strokeStyle = 'rgba(40,30,20,.8)'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(p.x, p.y + 10); ctx.lineTo(p.x, p.y + 24); ctx.stroke();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = '#fff';
      ctx.font = `40px ${FONT}`; ctx.fillText('✈️', p.x, p.y);
      ctx.font = `32px ${FONT}`; ctx.fillText('🎁', p.x, p.y + 36);
    }
    ctx.restore();
    ctx.textBaseline = 'alphabetic';
  }

  /** Confetti burst where a Paper Plane Gift was caught (4 frames at 16 fps). */
  giftBurst(at: Pos): void { this.giftBursts.push({ x: at.x, y: at.y, t: 0 }); }

  private drawGiftBursts(dt: number): void {
    const img = this.giftArt.burst;
    for (let i = this.giftBursts.length - 1; i >= 0; i--) {
      const b = this.giftBursts[i]!;
      b.t += dt;
      const f = Math.floor(b.t * 16);
      if (f >= 4) { this.giftBursts.splice(i, 1); continue; }
      if (!ready(img)) continue;
      const s = 150;
      this.ctx.drawImage(img, f * 256, 0, 256, 256, b.x - s / 2, b.y - s / 2, s, s);
    }
  }


  /** The Hero's feet position and on-screen height: front and centre, still small next to the Giants. */
  private playerAnchor(): { x: number; h: number } {
    return { x: this.cx(), h: this.baseRadius() * ARENA.heroScale };
  }

  private playerIdle = loadSprite(PLAYER_IDLE.src);
  private playerAttack = loadSprite(PLAYER_ATTACK.src);

  /**
   * Plays the attack strip on a hit. Hits during the wind-up ride the swing already under way;
   * hits after the strike chain straight back into the wind-up so fast tapping keeps slashing.
   */
  private startAttack(): void {
    if (this.attackF < 0) this.attackF = 0;
    else if (this.attackF >= 4) this.attackF = 2;
  }

  /**
   * The Hero (Tuck): painted idle and attack strips (docs/DESIGN.md §6.1), with the code-drawn toy
   * knight as a fallback while they load.
   */
  private drawPlayer(): void {
    const { x, h } = this.playerAnchor();
    const gy = this.heroFloor();
    const t = performance.now() / 1000;
    const swing = Math.sin(this.playerSwing * Math.PI); // 0 -> 1 -> 0 over the swing
    const { ctx } = this;
    const u = h / 100; // drawing unit: the hero is 100u tall
    ctx.fillStyle = 'rgba(0,0,0,.25)';
    ctx.beginPath(); ctx.ellipse(x, gy + 2, 30 * u, 5 * u, 0, 0, Math.PI * 2); ctx.fill();
    const attacking = this.attackF >= 0 && ready(this.playerAttack);
    const img = attacking ? this.playerAttack : this.playerIdle;
    if (ready(img)) {
      const S = attacking ? PLAYER_ATTACK : PLAYER_IDLE, k = h / S.bodyH;
      const f = attacking ? Math.floor(this.attackF) : Math.floor(t * S.fps) % S.frames;
      ctx.drawImage(img, f * S.cell, 0, S.cell, S.cell, x - S.footX * k, gy - S.footY * k, S.cell * k, S.cell * k);
      return;
    }
    ctx.save();
    ctx.translate(x + swing * 12 * u, gy + Math.sin(t * 3) * 1.5);
    ctx.rotate(swing * 0.1);
    ctx.lineJoin = 'round';
    // scarf (behind the body), fluttering to the left
    const flap = Math.sin(t * 6) * 4 * u;
    ctx.fillStyle = '#d8342c';
    ctx.beginPath(); ctx.moveTo(-6 * u, -64 * u);
    ctx.quadraticCurveTo(-30 * u, -62 * u + flap, -44 * u, -50 * u - flap);
    ctx.lineTo(-38 * u, -44 * u - flap); ctx.quadraticCurveTo(-24 * u, -52 * u, -4 * u, -54 * u); ctx.closePath(); ctx.fill();
    // legs
    ctx.fillStyle = '#5f6b7a';
    this.roundRect(-14 * u, -26 * u, 11 * u, 26 * u, 3 * u); ctx.fill();
    this.roundRect(3 * u, -26 * u, 11 * u, 26 * u, 3 * u); ctx.fill();
    ctx.fillStyle = '#3b4250';
    this.roundRect(-16 * u, -6 * u, 14 * u, 7 * u, 3 * u); ctx.fill();
    this.roundRect(2 * u, -6 * u, 15 * u, 7 * u, 3 * u); ctx.fill();
    // silver armor torso
    const armor = ctx.createLinearGradient(0, -62 * u, 0, -24 * u);
    armor.addColorStop(0, '#eef2f7'); armor.addColorStop(1, '#9aa6b5');
    ctx.fillStyle = armor; ctx.strokeStyle = 'rgba(20,24,34,.55)'; ctx.lineWidth = 2;
    this.roundRect(-17 * u, -62 * u, 34 * u, 38 * u, 9 * u); ctx.fill(); ctx.stroke();
    // scarf wrap at the neck
    ctx.fillStyle = '#e8453c';
    this.roundRect(-15 * u, -66 * u, 30 * u, 9 * u, 4 * u); ctx.fill();
    // head: skin + brown messy hair + big eyes
    ctx.fillStyle = '#ffd6b0';
    ctx.beginPath(); ctx.arc(0, -80 * u, 16 * u, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#6b3d22';
    ctx.beginPath(); ctx.arc(0, -84 * u, 16 * u, Math.PI * 1.05, Math.PI * 1.95); ctx.lineTo(14 * u, -78 * u);
    for (let i = 0; i < 5; i++) ctx.lineTo((10 - i * 6) * u, (-92 - (i % 2) * 6) * u);
    ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#1a1726';
    ctx.beginPath(); ctx.ellipse(6 * u, -79 * u, 2.4 * u, 3.4 * u, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(12 * u, -79 * u, 2.2 * u, 3.2 * u, 0, 0, Math.PI * 2); ctx.fill();
    // arm + cyan crystal blade (raised on the swing)
    ctx.save();
    ctx.translate(14 * u, -46 * u);
    ctx.rotate(-0.9 + swing * 1.5);
    ctx.fillStyle = '#9aa6b5'; this.roundRect(-4 * u, -4 * u, 16 * u, 8 * u, 4 * u); ctx.fill();
    ctx.fillStyle = '#7a5230'; this.roundRect(10 * u, -3 * u, 7 * u, 6 * u, 2 * u); ctx.fill();
    ctx.fillStyle = '#c9a54a'; this.roundRect(16 * u, -7 * u, 4 * u, 14 * u, 2 * u); ctx.fill();
    const blade = ctx.createLinearGradient(20 * u, 0, 62 * u, 0);
    blade.addColorStop(0, '#bff4ff'); blade.addColorStop(1, '#3fb9ff');
    ctx.fillStyle = blade; ctx.shadowColor = '#5fd4ff'; ctx.shadowBlur = 12;
    ctx.beginPath(); ctx.moveTo(20 * u, -5 * u); ctx.lineTo(56 * u, -4 * u); ctx.lineTo(64 * u, 0); ctx.lineTo(56 * u, 4 * u); ctx.lineTo(20 * u, 5 * u); ctx.closePath(); ctx.fill();
    ctx.restore();
    ctx.restore();
  }


  /** Active pet hovers up-left of the monster and dashes at it when it strikes. */
  private drawPet(): void {
    const id = this.game.state.pets.active;
    if (!id) return;
    const pet = PET_BY_ID.get(id)!;
    const { ctx } = this;
    const t = performance.now() / 1000;
    const m = this.monsterCenter(), r = this.monsterRadius();
    const p = this.playerAnchor();
    const home = { x: p.x - p.h * 0.75, y: this.heroFloor() - p.h * 1.15 + Math.sin(t * 2.5) * 6 };
    const k = Math.sin(this.petLunge * Math.PI); // out and back
    const x = home.x + (m.x - home.x) * k * 0.6, y = home.y + (m.y - home.y) * k * 0.6;
    ctx.fillStyle = 'rgba(0,0,0,.18)';
    ctx.beginPath(); ctx.ellipse(home.x, this.heroFloor() + 2, 14, 4, 0, 0, Math.PI * 2); ctx.fill();
    ctx.save();
    ctx.shadowColor = pet.color; ctx.shadowBlur = 14;
    ctx.font = `30px ${FONT}`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillStyle = '#fff'; // opaque, or the emoji inherits the shadow's transparency
    ctx.fillText(pet.icon, x, y);
    ctx.restore();
    ctx.textBaseline = 'alphabetic';
  }

  /** A coin in flight: the painted coin, spinning (squashed sideways), smaller as it nears the pill. */
  private drawCoinArt(c: Coin): void {
    if (!ready(this.coinArt)) { this.drawCoin(c.x, c.y, 7); return; }
    const { ctx } = this, s = 22, spin = Math.abs(Math.cos(c.t * 9 + c.delay * 20));
    ctx.drawImage(this.coinArt, c.x - (s * Math.max(0.25, spin)) / 2, c.y - s / 2, s * Math.max(0.25, spin), s);
  }

  private drawCoin(x: number, y: number, r: number): void {
    const { ctx } = this;
    ctx.fillStyle = '#c98a12'; ctx.beginPath(); ctx.arc(x, y + 1, r, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#ffcc4d'; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#fff3b0'; ctx.beginPath(); ctx.arc(x - r * 0.3, y - r * 0.3, r * 0.3, 0, Math.PI * 2); ctx.fill();
  }

  /** Trimmed enemy/Giant sprites by path: undefined = not requested yet, null = loading or failed. */
  private monsterSprites = new Map<string, TrimmedSprite | null>();

  private monsterSprite(src: string): TrimmedSprite | null {
    const known = this.monsterSprites.get(src);
    if (known !== undefined) return known;
    this.monsterSprites.set(src, null);
    const img = loadSprite(src);
    img.onload = () => this.monsterSprites.set(src, trimSprite(img));
    return null;
  }

  private drawMonster(): void {
    const { ctx, W } = this;
    const m = this.game.monster, p = this.monsterCenter(), gy = this.monsterFloor();
    const room = biomeForStage(this.game.state.stage);
    // Warm up the whole room's art so the Giant doesn't flash as a blob while its big PNG loads.
    for (const other of [...(room.enemyImages ?? []), room.giantImage]) if (other) { this.monsterSprite(other); this.monsterSprite(hitImage(other)); }
    const src = monsterImage(room, m);
    const sprite = src ? this.monsterSprite(src) : null;
    const r = sprite ? this.monsterRadius() : this.blobRadius();
    const hue = m.seed * 37 % 360, eyes = 1 + (m.seed % 3);
    const t = performance.now() / 1000;
    const bob = Math.sin(t * 3) * 0.04;
    // Giants are single stills: a slow spring hop (squash on landing) keeps them alive.
    const hop = m.boss ? Math.abs(Math.sin(t * 2.2)) : 0, land = m.boss ? Math.max(0, 0.25 - hop) * 0.3 : 0;
    const squash = this.hitT * 0.12 + land;
    const appear = 1 - Math.pow(1 - this.bornT, 3);
    const die = m.dead ? this.dieT : 0;
    // Clamp: a zero/negative scale would make ellipse() throw.
    const sx = Math.max(0.001, (1 + bob + squash) * appear * (1 + die * 0.3));
    const sy = Math.max(0.001, (1 - bob - squash) * appear * (1 - die));

    ctx.save();
    ctx.globalAlpha = 1 - die;
    ctx.fillStyle = 'rgba(0,0,0,.25)';
    ctx.beginPath(); ctx.ellipse(p.x, gy + 2, r * 0.9 * sx, r * 0.18, 0, 0, Math.PI * 2); ctx.fill();

    ctx.translate(p.x, gy - hop * r * 0.07); ctx.scale(sx, sy);
    if (sprite) {
      // Same footprint as the blob: about 1.9r tall, no wider than 2.6r, feet on the floor line.
      const k = Math.min((r * 1.9) / sprite.img.height, (r * 2.6) / sprite.img.width);
      const dw = sprite.img.width * k, dh = sprite.img.height * k;
      // Hit pose (Batch 3): drawn in the idle sprite's frame, so the recoil happens in place.
      const hit = !m.dead && t - this.hitPoseAt < HIT_POSE_SEC && src ? this.monsterSprite(hitImage(src)) : null;
      const shown = hit ?? sprite;
      const x = -dw / 2 + (shown.x0 - sprite.x0) * k, y = -dh + (shown.y0 - sprite.y0) * k;
      const sw = shown.img.width * k, sh = shown.img.height * k;
      ctx.drawImage(shown.img, x, y, sw, sh);
      if (this.hitT > 0) { ctx.globalAlpha = this.hitT * (hit ? 0.25 : 0.6) * (1 - die); ctx.drawImage(shown.flash, x, y, sw, sh); }
      ctx.restore();
      return;
    }
    ctx.translate(0, -r * 0.95);
    const body = `hsl(${hue},60%,${m.boss ? 42 : 52}%)`;
    const dark = `hsl(${hue},60%,${m.boss ? 25 : 32}%)`;
    if (m.boss) {
      ctx.fillStyle = '#f4ecd8';
      for (const s of [-1, 1]) {
        ctx.beginPath(); ctx.moveTo(s * r * 0.45, -r * 0.7); ctx.quadraticCurveTo(s * r * 0.95, -r * 1.2, s * r * 0.75, -r * 1.35);
        ctx.quadraticCurveTo(s * r * 0.7, -r * 1.0, s * r * 0.25, -r * 0.85); ctx.fill();
      }
    }
    ctx.fillStyle = dark;
    for (const s of [-1, 1]) { ctx.beginPath(); ctx.ellipse(s * r * 0.45, r * 0.85, r * 0.25, r * 0.15, 0, 0, Math.PI * 2); ctx.fill(); }
    ctx.fillStyle = body; ctx.strokeStyle = dark; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.ellipse(0, 0, r, r * 0.92, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,.15)'; ctx.beginPath(); ctx.ellipse(-r * 0.35, -r * 0.4, r * 0.35, r * 0.2, -0.5, 0, Math.PI * 2); ctx.fill();
    const n = m.boss ? 2 : eyes;
    for (let i = 0; i < n; i++) {
      const ex = n === 1 ? 0 : (i / (n - 1) - 0.5) * r * (n === 2 ? 0.7 : 0.9);
      const er = r * (n === 1 ? 0.28 : 0.18);
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(ex, -r * 0.2, er, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#1a1726'; ctx.beginPath(); ctx.arc(ex + Math.sin(t) * er * 0.25, -r * 0.18, er * 0.5, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = dark; ctx.lineWidth = 5; ctx.beginPath();
      ctx.moveTo(ex - er, -r * 0.2 - er * 1.2); ctx.lineTo(ex + er, -r * 0.2 - er * (i < n / 2 ? 0.7 : 1.5)); ctx.stroke();
    }
    ctx.fillStyle = '#3a1020'; ctx.beginPath(); ctx.ellipse(0, r * 0.35, r * 0.4, r * 0.16, 0, 0, Math.PI); ctx.fill();
    ctx.fillStyle = '#fff';
    for (let i = -2; i <= 2; i++) {
      ctx.beginPath(); ctx.moveTo(i * r * 0.14 - r * 0.05, r * 0.35); ctx.lineTo(i * r * 0.14, r * 0.45); ctx.lineTo(i * r * 0.14 + r * 0.05, r * 0.35); ctx.fill();
    }
    if (m.boss) {
      ctx.fillStyle = '#ffcc4d'; ctx.strokeStyle = '#8a5c05'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(-r * 0.35, -r * 0.8); ctx.lineTo(-r * 0.4, -r * 1.15); ctx.lineTo(-r * 0.18, -r * 0.95); ctx.lineTo(0, -r * 1.25);
      ctx.lineTo(r * 0.18, -r * 0.95); ctx.lineTo(r * 0.4, -r * 1.15); ctx.lineTo(r * 0.35, -r * 0.8); ctx.closePath(); ctx.fill(); ctx.stroke();
    }
    if (this.hitT > 0) {
      ctx.globalAlpha = this.hitT * 0.6 * (1 - die); ctx.fillStyle = '#fff';
      ctx.beginPath(); ctx.ellipse(0, 0, r, r * 0.92, 0, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();

    // HP is shown by the HUD enemy bar (index.html #enemy-bar).
  }

  private fireShot(s: HeroSlot, x: number, y: number): void {
    const p = this.monsterCenter();
    this.shots.push({ x, y, tx: p.x + (Math.random() - 0.5) * 40, ty: p.y + (Math.random() - 0.5) * 40, t: 0,
      color: s.h.color, kind: GUARDIAN_ART[s.h.id]?.shot ?? (s.h.type === 'melee' ? 'slash' : 'orb'),
      image: GUARDIAN_ART[s.h.id]?.shotImage, impact: GUARDIAN_ART[s.h.id]?.impactImage, spin: GUARDIAN_ART[s.h.id]?.shotSpin });
  }

  private fx(src: string): HTMLImageElement | null {
    const known = this.fxImages.get(src);
    if (known !== undefined) return known;
    this.fxImages.set(src, null);
    const img = loadSprite(src);
    img.onload = () => this.fxImages.set(src, img);
    return null;
  }

  /** Glowing so volleys read against busy painted rooms. Orbs lob; slashes fly straight and spin. */
  private drawShot(s: Shot): void {
    const { ctx } = this;
    const lob = s.kind === 'slash' ? 0 : Math.sin(s.t * Math.PI) * 40;
    const x = s.x + (s.tx - s.x) * s.t, y = s.y + (s.ty - s.y) * s.t - lob;
    ctx.save();
    ctx.shadowColor = s.color; ctx.shadowBlur = 12;
    const painted = s.image ? this.fx(s.image) : null;
    if (painted) {
      const size = this.baseRadius() * 0.38;
      ctx.shadowBlur = 0; ctx.translate(x, y);
      if (s.spin) ctx.rotate(s.t * 9);
      else {
        // Point along the flight path (the art faces right); mirror instead of turning upside down.
        const dx = s.tx - s.x, dy = s.ty - s.y - Math.cos(s.t * Math.PI) * Math.PI * (s.kind === 'slash' ? 0 : 40);
        if (dx < 0) ctx.scale(-1, 1);
        ctx.rotate(Math.atan2(dy, Math.abs(dx)));
      }
      ctx.drawImage(painted, -size / 2, -size / 2, size, size);
    } else if (s.kind === 'brick') {
      // A 2x2 toy brick in the Knight's blue, tumbling end over end.
      ctx.shadowColor = 'rgba(255,255,255,.8)'; ctx.shadowBlur = 8;
      ctx.translate(x, y); ctx.rotate(s.t * 9);
      ctx.fillStyle = '#2f5fd0'; this.roundRect(-11, -7, 22, 14, 3); ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#1d3f98'; this.roundRect(-11, 3, 22, 4, 2); ctx.fill();
      ctx.fillStyle = '#4f7ff0';
      for (const sx of [-5.5, 5.5]) { this.roundRect(sx - 3.5, -11, 7, 5, 2); ctx.fill(); }
      ctx.strokeStyle = '#f2c14e'; ctx.lineWidth = 1.5; this.roundRect(-11, -7, 22, 14, 3); ctx.stroke();
    } else if (s.kind === 'slash') {
      ctx.translate(x, y); ctx.rotate(Math.atan2(s.ty - s.y, s.tx - s.x) + s.t * 6);
      ctx.strokeStyle = s.color; ctx.lineWidth = 6; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.arc(0, 0, 14, -1.1, 1.1); ctx.stroke();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.arc(0, 0, 14, -0.9, 0.9); ctx.stroke();
    } else {
      ctx.fillStyle = s.color; ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0; ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(x - 1.5, y - 1.5, 2.5, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }

  /** Loads a Guardian strip and measures its first frame; null while loading or if it failed. */
  private strip(src: string): Strip | null {
    const known = this.strips.get(src);
    if (known !== undefined) return known;
    this.strips.set(src, null);
    const img = loadSprite(src);
    img.onload = () => this.strips.set(src, { img, bodyH: visibleHeight(img, GUARDIAN_STRIP.cell) });
    return null;
  }

  private drawHero(s: HeroSlot): void {
    const { ctx } = this;
    const t = performance.now() / 1000;
    const art = GUARDIAN_ART[s.h.id];
    const idle = art?.idle ? this.strip(art.idle) : null;
    if (idle) { this.drawGuardianSprite(s, idle, art?.attack ? this.strip(art.attack) : null, t); return; }
    const lunge = this.lunge.get(s.h.id) ?? 0;
    const x = s.x + lunge * 10 * -s.side, y = s.y + Math.sin(t * 4 + s.phase * 6) * 2;
    ctx.fillStyle = 'rgba(0,0,0,.25)'; ctx.beginPath(); ctx.ellipse(s.x, s.y + 12, 14, 4, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = s.h.color; ctx.strokeStyle = 'rgba(0,0,0,.45)'; ctx.lineWidth = 2;
    this.roundRect(x - 11, y - 10, 22, 22, 7); ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#ffd9b3'; ctx.beginPath(); ctx.arc(x, y - 18, 10, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = s.h.color; ctx.beginPath(); ctx.arc(x, y - 21, 10, Math.PI, 0); ctx.fill();
    ctx.fillStyle = '#1a1726'; ctx.fillRect(x - 4 * s.side - 1, y - 18, 2.5, 3); ctx.fillRect(x - 1 * s.side - 1, y - 18, 2.5, 3);
    // weapon by hero type
    ctx.strokeStyle = s.h.type === 'spell' ? '#b98cff' : '#e8e8f0'; ctx.lineWidth = 3; ctx.beginPath();
    if (s.h.type === 'ranged') { ctx.arc(x - s.side * 14, y - 6, 9, s.side < 0 ? -1.2 : 1.9, s.side < 0 ? 1.2 : 4.3); }
    else { ctx.moveTo(x - s.side * 10, y); ctx.lineTo(x - s.side * 22, y - 14 - lunge * 6); }
    ctx.stroke();
    ctx.fillStyle = 'rgba(0,0,0,.55)'; this.roundRect(x - 13, y + 14, 26, 12, 6); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.font = `800 9px ${FONT}`; ctx.textAlign = 'center';
    ctx.fillText(String(this.game.heroLevel(s.h.id)), x, y + 23);
  }

  /**
   * Painted Guardian: about 0.8x the Hero's base height (smaller further back), feet on its arc place,
   * mirrored on the right of the enemy so it faces the fight.
   */
  private drawGuardianSprite(s: HeroSlot, idle: Strip, attack: Strip | null, t: number): void {
    const { ctx } = this;
    const S = GUARDIAN_STRIP;
    const feet = s.y + 12;
    const af = this.guardianAttackF.get(s.h.id);
    const strip = af !== undefined && attack ? attack : idle;
    const idleFrames = GUARDIAN_ART[s.h.id]?.idleFrames ?? S.idleFrames;
    const frame = strip === attack ? Math.floor(af!) : Math.floor(t * S.idleFps + s.phase * idleFrames) % idleFrames;
    const h = this.baseRadius() * 0.8 * s.scale, k = h / idle.bodyH;
    ctx.fillStyle = 'rgba(0,0,0,.25)'; ctx.beginPath(); ctx.ellipse(s.x, feet, h * 0.3, h * 0.06, 0, 0, Math.PI * 2); ctx.fill();
    ctx.save();
    ctx.translate(s.x, feet);
    if (s.side > 0) ctx.scale(-1, 1);
    // Wide attack cells (body + FX) share the body's top-left origin, so the feet stay at footX.
    const cw = strip === attack ? GUARDIAN_ART[s.h.id]?.attackCell ?? S.cell : S.cell;
    ctx.drawImage(strip.img, frame * cw, 0, cw, S.cell, -S.footX * k, -S.footY * k, cw * k, S.cell * k);
    ctx.restore();
    ctx.fillStyle = 'rgba(0,0,0,.55)'; this.roundRect(s.x - 13, feet + 4, 26, 12, 6); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.font = `800 9px ${FONT}`; ctx.textAlign = 'center';
    ctx.fillText(String(this.game.heroLevel(s.h.id)), s.x, feet + 13);
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const { ctx } = this;
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }
}

function step<T extends { life: number }>(arr: T[], dt: number, fn: (o: T) => void): void {
  for (let i = arr.length - 1; i >= 0; i--) {
    const o = arr[i]!;
    o.life -= dt;
    if (o.life <= 0) arr.splice(i, 1); else fn(o);
  }
}

/**
 * Crops a painted sprite to its visible pixels (AI art arrives with uneven padding), so it can be
 * sized and foot-anchored by its real bounds, and builds the white silhouette used for hit flashes.
 */
function trimSprite(img: HTMLImageElement): TrimmedSprite {
  const w = img.naturalWidth, h = img.naturalHeight;
  const src = document.createElement('canvas');
  src.width = w; src.height = h;
  const sctx = src.getContext('2d', { willReadFrequently: true })!;
  sctx.drawImage(img, 0, 0);
  const a = sctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (a[(y * w + x) * 4 + 3]! > 16) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
  }
  if (x1 < 0) { x0 = 0; y0 = 0; x1 = w - 1; y1 = h - 1; } // fully transparent: keep the whole image
  const cw = x1 - x0 + 1, ch = y1 - y0 + 1;
  const out = document.createElement('canvas');
  out.width = cw; out.height = ch;
  out.getContext('2d')!.drawImage(src, x0, y0, cw, ch, 0, 0, cw, ch);
  const flash = document.createElement('canvas');
  flash.width = cw; flash.height = ch;
  const fctx = flash.getContext('2d')!;
  fctx.drawImage(out, 0, 0);
  fctx.globalCompositeOperation = 'source-in';
  fctx.fillStyle = '#fff'; fctx.fillRect(0, 0, cw, ch);
  return { img: out, flash, x0, y0 };
}

/** Visible (alpha > 16) pixel height of the first cell of a strip. */
function visibleHeight(img: HTMLImageElement, cell: number): number {
  const c = document.createElement('canvas');
  c.width = cell; c.height = cell;
  const cctx = c.getContext('2d', { willReadFrequently: true })!;
  cctx.drawImage(img, 0, 0, cell, cell, 0, 0, cell, cell);
  const a = cctx.getImageData(0, 0, cell, cell).data;
  let top = cell, bottom = -1;
  for (let y = 0; y < cell; y++) {
    for (let x = 0; x < cell; x++) {
      if (a[(y * cell + x) * 4 + 3]! > 16) { if (y < top) top = y; bottom = y; break; }
    }
  }
  return bottom < 0 ? cell : bottom - top + 1;
}

function ready(img: HTMLImageElement): boolean {
  return img.complete && img.naturalWidth > 0;
}

function loadSprite(src: string): HTMLImageElement {
  const img = new Image();
  img.decoding = 'async';
  img.src = src;
  return img;
}

function rotateAbout(x: number, y: number, cx: number, cy: number, a: number): Pos {
  const c = Math.cos(a), s = Math.sin(a);
  return { x: cx + (x - cx) * c - (y - cy) * s, y: cy + (x - cx) * s + (y - cy) * c };
}
